import type { Case } from "./useRealtimeCases";

export const PAX_RANGES = [
  { value: "1-20", label: "1 à 20 pax", min: 1, max: 20 },
  { value: "21-50", label: "21 à 50 pax", min: 21, max: 50 },
  { value: "51-100", label: "51 à 100 pax", min: 51, max: 100 },
  { value: "101-200", label: "101 à 200 pax", min: 101, max: 200 },
  { value: "201+", label: "201 pax et plus", min: 201, max: Infinity },
];
export function caseBudget(c: Case): number | null {
  if (c.budget != null && Number.isFinite(c.budget) && c.budget >= 0) return c.budget;
  const pax = c.participants ?? c.pax;
  return c.budget_per_person != null && c.budget_per_person >= 0 && pax != null && pax > 0
    && Number.isFinite(c.budget_per_person * pax) ? c.budget_per_person * pax : null;
}
export function matchesDemandFilters(c: Case, paxFilter: string, budgetFilter: string) {
  const pax = c.participants ?? c.pax;
  const range = PAX_RANGES.find(r => r.value === paxFilter);
  const paxMatches = paxFilter === "all" || (paxFilter === "unknown" ? pax == null || pax <= 0
    : !!range && pax != null && pax >= range.min && pax <= range.max);
  const budget = caseBudget(c);
  const min = Number(budgetFilter);
  const budgetMatches = budgetFilter === "all" || (budgetFilter === "unknown" ? budget === null
    : budgetFilter === "over-15000" ? budget !== null && budget > 15000
    : budget !== null && Number.isFinite(min) && budget >= min && (min === 13500 ? budget <= 15000 : budget < min + 1500));
  return paxMatches && budgetMatches;
}

/* Curseurs libres du panneau « Filtres » (Paul, 04/11/2026). La borne haute d'un curseur veut dire « et plus ». */
/** Crans du curseur : fins sur les petites valeurs (les plus fréquentes), plus larges vers le haut. */
export interface RangeBounds { min: number; max: number; stops: readonly number[] }
function boundsFrom(segments: readonly [from: number, to: number, step: number][]): RangeBounds {
  const stops: number[] = [];
  for (const [from, to, step] of segments) for (let v = from; v <= to; v += step) if (stops[stops.length - 1] !== v) stops.push(v);
  return { min: stops[0], max: stops[stops.length - 1], stops };
}
export const PAX_BOUNDS = boundsFrom([[0, 100, 5], [110, 200, 10], [225, 500, 25]]);
export const BUDGET_BOUNDS = boundsFrom([[0, 5000, 250], [5500, 15000, 500], [16000, 30000, 1000], [32500, 50000, 2500]]);
/** Index du cran le plus proche d'une valeur. */
export function stopIndex(bounds: RangeBounds, value: number): number {
  let best = 0;
  bounds.stops.forEach((stop, index) => {
    if (Math.abs(stop - value) < Math.abs(bounds.stops[best] - value)) best = index;
  });
  return best;
}
export interface DemandRange { min: number; max: number; unknownOnly: boolean }
export const fullRange = (bounds: RangeBounds): DemandRange => ({ min: bounds.min, max: bounds.max, unknownOnly: false });
export function isRangeActive(range: DemandRange, bounds: RangeBounds): boolean {
  return range.unknownOnly || range.min > bounds.min || range.max < bounds.max;
}
/** Curseur au repos : tout passe, y compris les dossiers non renseignés. Curseur déplacé : seulement les valeurs connues dans l'intervalle. */
export function matchesRange(value: number | null, range: DemandRange, bounds: RangeBounds): boolean {
  if (range.unknownOnly) return value === null;
  if (!isRangeActive(range, bounds)) return true;
  if (value === null) return false;
  return value >= range.min && (range.max >= bounds.max || value <= range.max);
}
export function casePax(c: Case): number | null {
  const pax = c.participants ?? c.pax;
  return pax != null && Number.isFinite(pax) && pax > 0 ? pax : null;
}
export function matchesDemandRanges(c: Case, pax: DemandRange, budget: DemandRange): boolean {
  return matchesRange(casePax(c), pax, PAX_BOUNDS) && matchesRange(caseBudget(c), budget, BUDGET_BOUNDS);
}
/** Résumé lisible : « Tous », « 20 à 50 pax », « 50 pax et plus », « Jusqu'à 1 500 € ». */
export type RangeUnit = "pax" | "euros";
const formatNumber = (value: number) => value.toLocaleString("fr-FR");
const SUFFIX: Record<RangeUnit, string> = { pax: " pax", euros: " €" };
export function formatRangeValue(value: number, unit: RangeUnit): string {
  return `${formatNumber(value)}${SUFFIX[unit]}`;
}
export function rangeSummary(range: DemandRange, bounds: RangeBounds, unit: RangeUnit, unknownLabel: string): string {
  if (range.unknownOnly) return unknownLabel;
  const lowOpen = range.min <= bounds.min;
  const highOpen = range.max >= bounds.max;
  if (lowOpen && highOpen) return "Tous";
  if (highOpen) return `${formatRangeValue(range.min, unit)} et plus`;
  if (lowOpen) return `Jusqu'à ${formatRangeValue(range.max, unit)}`;
  if (range.min === range.max) return formatRangeValue(range.min, unit);
  return `${formatNumber(range.min)} à ${formatRangeValue(range.max, unit)}`;
}
